import React, { forwardRef, useCallback, useRef } from 'react';
import { QSpacer, QBox, QFlex } from '..';
import styles from './DocumentPageLayout.module.css';
import { TableOfContents, TableOfContentsProps } from './TableOfContents';
import { BackToTop } from './BackToTop';
import { DataProps } from 'utils';

export type DocumentPageLayoutProps<T> = {
  children: React.ReactNode;
  contentId?: string;
  editorName?: string;
} & TableOfContentsProps<T> &
  DataProps;

export const DocumentPageLayout = forwardRef(
  <T,>(
    {
      children,
      'data-cy': dataCy,
      contentId,
      sections,
      editorName,
    }: DocumentPageLayoutProps<T>,
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLElement | null>(null);

    const handleRef = useCallback(
      (node: HTMLDivElement | null) => {
        scrollContainerRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref && 'current' in ref) {
          ref.current = node;
        }
      },
      [ref, scrollContainerRef],
    );

    return (
      <QFlex
        flexShrink={1}
        flexGrow={1}
        justifyContent="space-between"
        bg="gray.50"
        border="1px"
        borderColor="gray.200"
        borderRadius="4px"
        ref={handleRef}
        className={styles['document-page-layout-content-container']}
        overflowY="hidden"
        overflowX="hidden"
      >
        <QFlex
          direction="column"
          flexShrink={1}
          flexGrow={1}
          ref={containerRef}
          className={styles['document-page-layout-toc-container']}
          style={{ minWidth: '48px' }}
          position="relative"
        >
          <TableOfContents sections={sections} editorName={editorName} />
          <QSpacer height="100%" />
          <BackToTop parentRef={containerRef} />
        </QFlex>
        <QFlex position="relative" height="fit-content">
          <QBox
            my={4}
            ml={0}
            border="1px"
            borderColor="gray.200"
            boxShadow="md"
            id={contentId}
            data-cy={dataCy}
            className={`${styles['document-page-layout-section-content']} document`}
          >
            {children}
          </QBox>
        </QFlex>
        <QBox
          className={styles['document-page-layout-sidebar']}
          style={{ minWidth: '48px' }}
        />
      </QFlex>
    );
  },
);

DocumentPageLayout.displayName = 'DocumentPageLayout';
